import React, { useState, useMemo } from 'react';
import TopNavbar from '../../../home/TopNavbar';

// Data parsed cleanly from your JEE Main chemistry weightage table
const rawChemistryData = [
  // Class XIth Chemistry
  { class: 'Class XIth Chemistry', sno: 1, chapter: 'Atomic Structure', weightage: 3.34 },
  { class: 'Class XIth Chemistry', sno: 2, chapter: 'Some Basic Concepts of Chemistry', weightage: 2.82 },
  { class: 'Class XIth Chemistry', sno: 3, chapter: 'Classification of Elements and Periodicity in Properties', weightage: 1.93 },
  { class: 'Class XIth Chemistry', sno: 4, chapter: 'Chemical Bonding and Molecular Structure', weightage: 3.34 },
  { class: 'Class XIth Chemistry', sno: 5, chapter: 'States of Matter', weightage: 1.89 },
  { class: 'Class XIth Chemistry', sno: 6, chapter: 'Chemical Thermodynamics and Energetics', weightage: 3.65 },
  { class: 'Class XIth Chemistry', sno: 7, chapter: 'Redox Reaction', weightage: 1.75 },
  { class: 'Class XIth Chemistry', sno: 8, chapter: 'Equilibrium', weightage: 4.44 },
  { class: 'Class XIth Chemistry', sno: 9, chapter: 'The p-Block Elements', weightage: 1.79 },
  { class: 'Class XIth Chemistry', sno: 10, chapter: 'Some Basic Principles and Techniques: IUPAC Nomenclature', weightage: 0.10 },
  { class: 'Class XIth Chemistry', sno: 11, chapter: 'Some Basic Principles and Techniques: Isomerism', weightage: 0.27 },
  { class: 'Class XIth Chemistry', sno: 12, chapter: 'Some Basic Principles and Techniques: General Organic Chemistry', weightage: 1.20 },
  { class: 'Class XIth Chemistry', sno: 13, chapter: 'Hydrocarbons', weightage: 3.03 },
  { class: 'Class XIth Chemistry', sno: 14, chapter: 'Purification and Analysis of Organic Compounds', weightage: 1.94 },
  { class: 'Class XIth Chemistry', sno: 15, chapter: 'Hydrogen', weightage: 2.82 },
  { class: 'Class XIth Chemistry', sno: 16, chapter: 's-Block Elements', weightage: 4.16 },
  { class: 'Class XIth Chemistry', sno: 17, chapter: 'Environmental Chemistry', weightage: 2.99 },

  // Class XIIth Chemistry
  { class: 'Class XIIth Chemistry', sno: 1, chapter: 'Solutions', weightage: 4.54 },
  { class: 'Class XIIth Chemistry', sno: 2, chapter: 'Electrochemistry', weightage: 3.30 },
  { class: 'Class XIIth Chemistry', sno: 3, chapter: 'Chemical Kinetics', weightage: 3.61 },
  { class: 'Class XIIth Chemistry', sno: 4, chapter: 'Haloalkanes and Haloarenes', weightage: 2.65 },
  { class: 'Class XIIth Chemistry', sno: 5, chapter: 'Alcohols, Phenols and Ethers', weightage: 3.54 },
  { class: 'Class XIIth Chemistry', sno: 6, chapter: 'Aldehydes, Ketones and Carboxylic Acids', weightage: 5.95 },
  { class: 'Class XIIth Chemistry', sno: 7, chapter: 'Amines', weightage: 4.40 },
  { class: 'Class XIIth Chemistry', sno: 8, chapter: 'Biomolecules', weightage: 3.99 },
  { class: 'Class XIIth Chemistry', sno: 9, chapter: 'Coordination Compounds', weightage: 5.33 },
  { class: 'Class XIIth Chemistry', sno: 10, chapter: 'The p-block Elements (Group 15 to 18)', weightage: 3.68 },
  { class: 'Class XIIth Chemistry', sno: 11, chapter: 'The d and f-Block Elements', weightage: 4.69 },
  { class: 'Class XIIth Chemistry', sno: 12, chapter: 'Principles of Inorganic Qualitative Analysis: Salt Analysis', weightage: 0.50 },
  { class: 'Class XIIth Chemistry', sno: 13, chapter: 'The Solid State', weightage: 1.72 },
  { class: 'Class XIIth Chemistry', sno: 14, chapter: 'Surface Chemistry', weightage: 2.99 },
  { class: 'Class XIIth Chemistry', sno: 15, chapter: 'General Principles and Processes of Isolation of Metals', weightage: 3.37 },
  { class: 'Class XIIth Chemistry', sno: 16, chapter: 'Polymers', weightage: 2.06 },
  { class: 'Class XIIth Chemistry', sno: 17, chapter: 'Chemistry in Everyday Life', weightage: 2.20 },
];

export default function ChapterWiseWeightageChemistryTopics() {
  const [selectedClass, setSelectedClass] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('weightage-desc');

  const maxWeightage = useMemo(() => Math.max(...rawChemistryData.map((i) => i.weightage)), []);

  const filteredData = useMemo(() => {
    return rawChemistryData
      .filter((item) => {
        const matchesClass = selectedClass === 'ALL' || item.class === selectedClass;
        const matchesSearch = item.chapter.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesClass && matchesSearch;
      })
      .sort((a, b) => {
        if (sortBy === 'weightage-desc') return b.weightage - a.weightage;
        if (sortBy === 'weightage-asc') return a.weightage - b.weightage;
        if (sortBy === 'alphabetical') return a.chapter.localeCompare(b.chapter);
        return 0;
      });
  }, [selectedClass, searchTerm, sortBy]);

  const stats = useMemo(() => {
    const class11Total = rawChemistryData
      .filter((d) => d.class === 'Class XIth Chemistry')
      .reduce((acc, curr) => acc + curr.weightage, 0);
    const class12Total = rawChemistryData
      .filter((d) => d.class === 'Class XIIth Chemistry')
      .reduce((acc, curr) => acc + curr.weightage, 0);
    const highWeightCount = rawChemistryData.filter((d) => d.weightage >= 4.0).length;
    const topTopic = [...rawChemistryData].sort((a, b) => b.weightage - a.weightage)[0];
    return { class11Total, class12Total, highWeightCount, topTopic };
  }, []);

  return (
    <>
      <style>{`
        .jeew-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 24px 16px;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          color: #1f2937;
          background-color: #f9fafb;
          min-height: 100vh;
          margin-top: 50px;
          margin-bottom: 50px;
        }
        .jeew-header {
          text-align: center;
          margin-bottom: 24px;
        }
        .jeew-header h1 {
          font-size: 1.85rem;
          font-weight: 800;
          color: #111827;
          margin: 0 0 8px 0;
        }
        .jeew-header p {
          color: #4b5563;
          font-size: 0.95rem;
          line-height: 1.5;
          max-width: 750px;
          margin: 0 auto;
        }
        .jeew-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .jeew-stat-card {
          background: #ffffff;
          padding: 16px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid #e5e7eb;
        }
        .jeew-stat-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #6b7280;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .jeew-stat-value {
          font-size: 1.35rem;
          font-weight: 700;
          color: #7c3aed;
        }
        .jeew-stat-sub {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 2px;
        }
        .jeew-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: space-between;
          align-items: center;
          background: #ffffff;
          padding: 16px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          margin-bottom: 20px;
        }
        .jeew-tabs {
          display: flex;
          gap: 6px;
        }
        .jeew-tab-btn {
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          border: 1px solid #d1d5db;
          background: #f3f4f6;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }
        .jeew-tab-btn.active {
          background: #7c3aed;
          color: #ffffff;
          border-color: #7c3aed;
        }
        .jeew-filter-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .jeew-input, .jeew-select {
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid #d1d5db;
          font-size: 0.85rem;
          background: #fff;
          color: #1f2937;
        }
        .jeew-table-wrapper {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow-x: auto;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .jeew-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          font-size: 0.9rem;
        }
        .jeew-table th, .jeew-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
        }
        .jeew-table th {
          background: #f8fafc;
          font-weight: 600;
          color: #374151;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .jeew-bar-container {
          background: #e5e7eb;
          height: 8px;
          border-radius: 4px;
          width: 120px;
          overflow: hidden;
        }
        .jeew-bar-fill {
          height: 100%;
          background: #7c3aed;
          border-radius: 4px;
        }
        .jeew-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .jeew-badge-high {
          background: #ede9fe;
          color: #6d28d9;
        }
        .jeew-badge-class {
          background: #f1f5f9;
          color: #475569;
          font-size: 0.7rem;
        }
        .jeew-empty {
          text-align: center;
          padding: 32px;
          color: #6b7280;
        }
      `}</style>

      <div className="jeew-container">

              <TopNavbar name="Chemistry Weightage" ></TopNavbar>

        <header className="jeew-header">
          <h3 style={{color: "green"}}>JEE Main Past 5 Years Chapter-wise Weightage for Chemistry</h3>
          <p>
            Below is a table summarising the chapter-wise weightage for Chemistry based on the last five years of JEE Main. This helps in identifying important chapters across Organic, Inorganic, and Physical Chemistry.
          </p>
        </header>

        <div className="jeew-stats-grid">
          <div className="jeew-stat-card">
            <div className="jeew-stat-label">Top Scoring Topic</div>
            <div className="jeew-stat-value">{stats.topTopic?.weightage}%</div>
            <div className="jeew-stat-sub">{stats.topTopic?.chapter}</div>
          </div>
          <div className="jeew-stat-card">
            <div className="jeew-stat-label">High-Impact Topics (&gt;4%)</div>
            <div className="jeew-stat-value">{stats.highWeightCount} Chapters</div>
            <div className="jeew-stat-sub">High priority scoring zones</div>
          </div>
          <div className="jeew-stat-card">
            <div className="jeew-stat-label">Class XIth Weight Sum</div>
            <div className="jeew-stat-value">{stats.class11Total.toFixed(2)}%</div>
            <div className="jeew-stat-sub">17 total chapters tracked</div>
          </div>
          <div className="jeew-stat-card">
            <div className="jeew-stat-label">Class XIIth Weight Sum</div>
            <div className="jeew-stat-value">{stats.class12Total.toFixed(2)}%</div>
            <div className="jeew-stat-sub">17 total chapters tracked</div>
          </div>
        </div>

        <div className="jeew-controls">
          <div className="jeew-tabs">
            <button
              className={`jeew-tab-btn ${selectedClass === 'ALL' ? 'active' : ''}`}
              onClick={() => setSelectedClass('ALL')}
            >
              All Classes
            </button>
            <button
              className={`jeew-tab-btn ${selectedClass === 'Class XIth Chemistry' ? 'active' : ''}`}
              onClick={() => setSelectedClass('Class XIth Chemistry')}
            >
              Class XIth
            </button>
            <button
              className={`jeew-tab-btn ${selectedClass === 'Class XIIth Chemistry' ? 'active' : ''}`}
              onClick={() => setSelectedClass('Class XIIth Chemistry')}
            >
              Class XIIth
            </button>
          </div>

          <div className="jeew-filter-group">
            <input
              type="text"
              placeholder="Search chapters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="jeew-input"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="jeew-select"
            >
              <option value="weightage-desc">Weightage: High to Low</option>
              <option value="weightage-asc">Weightage: Low to High</option>
              <option value="alphabetical">Alphabetical (A-Z)</option>
            </select>
          </div>
        </div>

        <div className="jeew-table-wrapper">
          <table className="jeew-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>S. No.</th>
                <th>Chapter Name</th>
                <th>Weightage Share</th>
                <th>Relative Weight</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((row, idx) => {
                  const percentWidth = Math.min(100, (row.weightage / maxWeightage) * 100);
                  return (
                    <tr key={`${row.class}-${row.sno}-${idx}`}>
                      <td>
                        <span className="jeew-badge jeew-badge-class">
                          {row.class.replace(' Chemistry', '')}
                        </span>
                      </td>
                      <td>{row.sno}</td>
                      <td><strong>{row.chapter}</strong></td>
                      <td>
                        <span
                          className={`jeew-badge ${
                            row.weightage >= 4.0 ? 'jeew-badge-high' : ''
                          }`}
                        >
                          {row.weightage.toFixed(2)}%
                        </span>
                      </td>
                      <td>
                        <div className="jeew-bar-container">
                          <div
                            className="jeew-bar-fill"
                            style={{ width: `${percentWidth}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="jeew-empty">
                    No matching chapters found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}